import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetHeaderCloseButton,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import styles from './ScopeChangeRequestActionSelectionDialog.module.css'
import { ToggleGroup, ToggleItem } from '@/components/ToggleGroup'
import { byDesign } from '@/infra/design'
import imgSendPromptMessageUrl from '@/assets/misc/img_send-prompt-message.svg'
import TeamRolesIllustration from '@/components/SpotIllustration/TeamRolesIllustration'
import { type ScopeChangeRequestActionType } from './ScopeChangeRequestActionSelectionDialogViewModel'

export interface ScopeChangeRequestActionSelectionDialogProps {
  showing: boolean
  onClose: () => unknown
  corporateMode: boolean
  actionType?: ScopeChangeRequestActionType
  onActionChange: (actionType: ScopeChangeRequestActionType) => void
  onSubmit: () => unknown
  isSubmitPending: boolean
}

export function ScopeChangeRequestActionSelectionDialog({
  showing,
  onClose,
  corporateMode,
  actionType,
  onActionChange,
  onSubmit,
  isSubmitPending,
}: ScopeChangeRequestActionSelectionDialogProps) {
  const testId = 'scope-change-request-action-selection'

  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader>
        <div className={styles.closeButtonContainer}>
          <ModalSheetHeaderCloseButton testId={`${testId}-close-dialog`} />
        </div>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          {!corporateMode && (
            <ModalSheetContentIllustration
              src={imgSendPromptMessageUrl}
              illustration={byDesign(undefined, TeamRolesIllustration)}
              alt="Confirm job scope updates"
            />
          )}
          <ModalSheetContentTitle>
            Confirm job scope updates
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Please choose your preferred action in order to proceed. The client
            will not be notified if you accept on their behalf.
          </ModalSheetContentDescription>
          <ToggleGroup stretch>
            <ToggleItem
              toggled={actionType === 'ApproveAsAdmin'}
              onChange={() => onActionChange('ApproveAsAdmin')}
              testId={`${testId}-accept-as-admin`}
            >
              Accept changes
            </ToggleItem>
            <ToggleItem
              toggled={actionType === 'ApproveOnBehalfOfClient'}
              onChange={() => onActionChange('ApproveOnBehalfOfClient')}
              testId={`${testId}-accept-on-behalf-of-client`}
            >
              Accept on behalf of the client
            </ToggleItem>
          </ToggleGroup>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Save"
            onPress={onSubmit}
            isPending={isSubmitPending}
            isDisabled={isSubmitPending}
            variant="primary"
            testId={`${testId}-save`}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
